/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

function PosCoupon({ reload, setBlockScan, sizeButton, buttonLoading, setButtonLoading }) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [notify, setNotify] = useState(null);
  const [txtSearch, setTxtSearch] = useState('');
  const txtCoupon = useRef(null);
  const btnRef = useRef(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  useEffect(() => {
    txtCoupon.current?.focus();
  }, [modalAdd]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const close = () => {
    setModalAdd(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const searchCoupon = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/coupon`, app.token, {
      lang: app.lang,
      search: txtSearch,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          reload();
          close();
          showNotify('เพิ่มส่วนลดแล้ว', 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify('ไม่พบข้อมูล', 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const init = () => {
    setTxtSearch('');
    setBlockScan(true);
    setModalAdd(true);
  };

  const renderInfo = () => {
    return (
      <MDBox>
        <MDBox
          noValidate
          component='form'
          role='form'
          onSubmit={(e) => {
            e.preventDefault();
            searchCoupon();
          }}
        >
          <MDInput
            name='searchform'
            type='search'
            fullWidth
            onChange={(e) => setTxtSearch(e.target.value)}
            value={txtSearch}
            InputProps={{
              inputRef: txtCoupon,
              startAdornment: (
                <InputAdornment position='start'>
                  <span>รหัส :</span>
                </InputAdornment>
              ),
              autoComplete: 'off',
            }}
          />
        </MDBox>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          <i className='fad fa-file-times color-danger' /> &nbsp; คูปอง
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderInfo()}</DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={close}>
                <Icon>close</Icon>&nbsp;ยกเลิก
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='text' color='info' onClick={searchCoupon} disabled={loading}>
                {loading ? <CircularProgress size={18} color='inherit' /> : <Icon>check</Icon>}
                &nbsp; ตรวจสอบ
              </MDButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <Tooltip title='Coupon'>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={init}
          ref={btnRef}
          disabled={buttonLoading}
        >
          <i className='fad fa-tags' /> &nbsp; คูปอง
        </MDButton>
      </Tooltip>
      {notify}

      {renderAdd()}
    </MDBox>
  );
}

PosCoupon.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosCoupon;
